import { useQuery } from '@tanstack/vue-query';
import { mergeQueryOptions, type QueryOptions } from '@/modules/query/utils';
import { request } from '@/api/request';
import type { ActivityItem } from '@@/packages/shared/types/models/activity';
import { ASSOCIATED_ACTIVITY_QUERY } from './queryKeys';

type AssociatedActivityItemsResponse = { activityItems: ActivityItem[] };

type AssociatedActivityParams =
  | { connectionID: string; associatedID?: string | null }
  | { connectionID?: string | null; associatedID: string };

const fetchAssociatedActivityItems = (params: AssociatedActivityParams) =>
  request<AssociatedActivityItemsResponse>('GET', `/activity/associated`, undefined, params);

export function useAssociatedActivityItems(
  data: MaybeRefOrGetter<AssociatedActivityParams>,
  options?: QueryOptions,
) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [ASSOCIATED_ACTIVITY_QUERY, toRef(data)] as const,
      async queryFn({ queryKey }) {
        const res = await fetchAssociatedActivityItems(queryKey[1]);
        return res.data;
      },
    }),
  );

  const activityItems = computed(() => query.data.value?.activityItems ?? []);

  return { ...query, query, activityItems };
}

type ConnectionActivityItemsResponse = { activityItems: ActivityItem[] };

const fetchConnectionActivityItems = (
  connectionID: string,
  limit: number,
  fromDate?: string,
  toDate?: string,
  excluding?: string[],
) =>
  request<ConnectionActivityItemsResponse>('GET', `/activity/connection/${connectionID}`, null, {
    limit,
    fromDate,
    toDate,
    excluding,
  });

const api = {
  fetchAssociatedActivityItems,
  fetchConnectionActivityItems,
};
export default api;

export const replaceAPI = (newApi: Record<string, any>) => {
  Object.keys(newApi).forEach((i) => {
    (api as any)[i] = newApi[i];
  });
};
